import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const NEXT = { requested: "confirmed", confirmed: "completed" };
const statusStyle = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-sky-100 text-sky-800",
  cancelled: "bg-slate-200 text-slate-600",
};

export const AppointmentsManager = () => {
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/appointments");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/appointments/${id}?status=${status}`);
    toast.success(`Marked as ${status}.`);
    load();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="admin-appointments">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-lg font-medium text-slate-900">Appointment requests ({items.length})</h2>
        <p className="text-sm text-slate-500 mt-1">Newest first. Call the patient, then confirm here.</p>
      </div>
      {items.length === 0 ? (
        <p className="p-6 text-sm text-slate-500" data-testid="admin-appointments-empty">
          No appointment requests yet.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {items.map((a) => (
            <li
              key={a.id}
              data-testid={`admin-appointment-row-${a.id}`}
              className="p-6 flex flex-wrap gap-5 items-start justify-between"
            >
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="font-medium text-slate-900">{a.patient_name}</p>
                  <Badge className={`rounded-full border-0 ${statusStyle[a.status]}`}>{a.status}</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-2">
                  {a.service} · {a.date} at {a.time_slot}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {a.phone} · {a.user_email}
                </p>
                {a.notes && <p className="text-sm text-slate-500 mt-2 max-w-xl">{a.notes}</p>}
              </div>
              <div className="flex gap-2">
                {NEXT[a.status] && (
                  <Button
                    className="rounded-md bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
                    onClick={() => setStatus(a.id, NEXT[a.status])}
                    data-testid={`admin-appointment-advance-${a.id}`}
                  >
                    Mark {NEXT[a.status]}
                  </Button>
                )}
                {a.status !== "cancelled" && (
                  <Button
                    variant="outline"
                    className="rounded-md"
                    onClick={() => setStatus(a.id, "cancelled")}
                    data-testid={`admin-appointment-cancel-${a.id}`}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
